'use client'

import { env } from 'next-runtime-env'
import { useEffect } from 'react'
import type { InvestorRelationsConfigContextType } from '@/utils/investorRelations'
import type { IRCompanyConfig } from '@/utils/investorRelations'

interface InvestorRelationsPageHeadProps {
  useContextFunction: () => InvestorRelationsConfigContextType
  isPreview?: boolean
  organisationId?: string
}

export function InvestorRelationsPageHead({
  useContextFunction,
  isPreview = false,
  organisationId = undefined,
}: InvestorRelationsPageHeadProps) {
  const NEXT_PUBLIC_ASSIDUOUS_API_URL = env('NEXT_PUBLIC_ASSIDUOUS_API_URL')
  const { config } = useContextFunction()

  useEffect(() => {
    if (!config) return

    const companyConfig = ((config.companyConfig as IRCompanyConfig) ||
      {}) as IRCompanyConfig
    const companyName = companyConfig.companyName
    const companySlug = config.companySlugPublished

    // Set document title
    if (companyName) {
      document.title = `${companyName} | Investor Relations`
    }

    // Determine logo URL based on preview mode
    let logoUrl = companyConfig.logoUrl || ''
    if (isPreview && organisationId) {
      // Use proxy route for preview mode
      logoUrl = `/api/proxy/admin/investor-relations/${organisationId}/logo`
    } else if (!logoUrl && companySlug) {
      // Use public API URL for published pages
      logoUrl = `${NEXT_PUBLIC_ASSIDUOUS_API_URL}/investor-relations/${companySlug}/logo`
    }

    if (logoUrl) {
      // Remove existing favicon links
      const existingFavicons = document.querySelectorAll(
        'link[rel="icon"], link[rel="shortcut icon"], link[rel="apple-touch-icon"]'
      )
      for (const link of existingFavicons) {
        document.head.removeChild(link)
      }

      // Create and add favicon (no sizes to avoid multiple favicon requests)
      const faviconLink = document.createElement('link')
      faviconLink.rel = 'icon'
      faviconLink.type = 'image/png'
      faviconLink.href = logoUrl
      document.head.appendChild(faviconLink)

      // Add apple-touch-icon for iOS devices (typically larger, 180x180)
      const appleTouchIcon = document.createElement('link')
      appleTouchIcon.rel = 'apple-touch-icon'
      appleTouchIcon.href = logoUrl
      appleTouchIcon.setAttribute('sizes', '180x180')
      document.head.appendChild(appleTouchIcon)

      // Remove existing Open Graph and Twitter meta tags
      const existingMetaTags = document.querySelectorAll(
        'meta[property^="og:"], meta[name^="twitter:"]'
      )
      for (const tag of existingMetaTags) {
        document.head.removeChild(tag)
      }

      const twitterImage = document.createElement('meta')
      twitterImage.setAttribute('name', 'twitter:image')
      twitterImage.setAttribute('content', logoUrl)
      document.head.appendChild(twitterImage)

      const ogImage = document.createElement('meta')
      ogImage.setAttribute('property', 'og:image')
      ogImage.setAttribute('content', logoUrl)
      document.head.appendChild(ogImage)

      const ogImageWidth = document.createElement('meta')
      ogImageWidth.setAttribute('property', 'og:image:width')
      ogImageWidth.setAttribute('content', '1200')
      document.head.appendChild(ogImageWidth)

      const ogImageHeight = document.createElement('meta')
      ogImageHeight.setAttribute('property', 'og:image:height')
      ogImageHeight.setAttribute('content', '630')
      document.head.appendChild(ogImageHeight)
    }

    if (companyName) {
      const pageUrl = window.location.href

      const ogTitle = document.createElement('meta')
      ogTitle.setAttribute('property', 'og:title')
      ogTitle.setAttribute('content', `${companyName} | Investor Relations`)
      document.head.appendChild(ogTitle)

      const ogDescription = document.createElement('meta')
      ogDescription.setAttribute('property', 'og:description')
      ogDescription.setAttribute(
        'content',
        `Investor Relations page for ${companyName}`
      )
      document.head.appendChild(ogDescription)

      const ogType = document.createElement('meta')
      ogType.setAttribute('property', 'og:type')
      ogType.setAttribute('content', 'website')
      document.head.appendChild(ogType)

      if (pageUrl) {
        const ogUrl = document.createElement('meta')
        ogUrl.setAttribute('property', 'og:url')
        ogUrl.setAttribute('content', pageUrl)
        document.head.appendChild(ogUrl)
      }

      // Add Twitter Card meta tags
      const twitterCard = document.createElement('meta')
      twitterCard.setAttribute('name', 'twitter:card')
      twitterCard.setAttribute('content', 'summary_large_image')
      document.head.appendChild(twitterCard)

      const twitterTitle = document.createElement('meta')
      twitterTitle.setAttribute('name', 'twitter:title')
      twitterTitle.setAttribute(
        'content',
        `${companyName} | Investor Relations`
      )
      document.head.appendChild(twitterTitle)

      const twitterDescription = document.createElement('meta')
      twitterDescription.setAttribute('name', 'twitter:description')
      twitterDescription.setAttribute(
        'content',
        `Investor Relations page for ${companyName}`
      )
      document.head.appendChild(twitterDescription)
    }

    // Cleanup function
    return () => {
      const allFavicons = document.querySelectorAll(
        'link[rel="icon"], link[rel="apple-touch-icon"]'
      )
      for (const link of allFavicons) {
        if (link.getAttribute('href') === logoUrl) {
          document.head.removeChild(link)
        }
      }

      const metaTagsToRemove = document.querySelectorAll(
        'meta[property^="og:"], meta[name^="twitter:"]'
      )
      for (const tag of metaTagsToRemove) {
        document.head.removeChild(tag)
      }
    }
  }, [config, NEXT_PUBLIC_ASSIDUOUS_API_URL, isPreview, organisationId])

  // Hotjar Tracking Code for IR Website Analytics
  useEffect(() => {
    const HOTJAR_SITE_ID = env('NEXT_PUBLIC_HOTJAR_SITE_ID')

    // Only load Hotjar if Site ID is configured
    if (!HOTJAR_SITE_ID) {
      return
    }

    // Check if Hotjar is already loaded
    interface HotjarFunction {
      (...args: unknown[]): void
      q: unknown[][]
    }
    interface WindowWithHotjar extends Window {
      hj?: HotjarFunction
      _hjSettings?: {
        hjid: string
        hjsv: number
      }
    }
    const windowWithHotjar = window as WindowWithHotjar
    if (windowWithHotjar.hj) {
      return
    }

    // Initialize Hotjar tracking script
    const hotjarQueue: unknown[][] = []
    const hjFunction: HotjarFunction = ((...args: unknown[]) => {
      hotjarQueue.push(args)
    }) as HotjarFunction
    hjFunction.q = hotjarQueue
    windowWithHotjar.hj = hjFunction

    const hotjarSettings = { hjid: HOTJAR_SITE_ID, hjsv: 6 }
    windowWithHotjar._hjSettings = hotjarSettings

    const headElement = document.getElementsByTagName('head')[0]
    if (headElement) {
      const scriptElement = document.createElement('script')
      scriptElement.async = true
      scriptElement.src = `https://static.hotjar.com/c/hotjar-${hotjarSettings.hjid}.js?sv=${hotjarSettings.hjsv}`
      headElement.appendChild(scriptElement)
    }
  }, [])

  return null
}
